import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import "./BigCommerceStoreModal.scss";
import OwButton from "../../../../shared/OwButton/OwButton";
import IErrorDTO from "../../../../../models/DTO/IErrorDTO";
import OwTitle from "../../../../shared/OwTitle/OwTitle";
import IBigCommerce from "../../../../../models/IBigCommerce";
import OwLabel from "../../../../shared/OwLabel/OwLabel";
import { OwFnInput } from "../../../../shared/OwFnInput/OwFnInput";
import INewBigCommerce from "../../../../../models/INewBigCommerce";
import ICatalog from "../../../../../models/ICatalog";
import EndpointsEnum from "../../../../../models/EndpointsEnum";
import IOnGetCatalogsDTO from "../../../../../models/DTO/IOnGetCatalogsDTO";
import IGetCatalogsDTO from "../../../../../models/DTO/IGetCatalogsDTO";
import UserContext from "../../../../../contexts/UserContext";
import SupplierContext from "../../../../../contexts/SupplierContext";
import OwSelectOption from "../../../../shared/OwSelectOption/OwSelectOption";
import OwCheckbox from "../../../../shared/OwCheckbox/OwCheckbox";
import IBigCommerceUpsertStoreDTO from "../../../../../models/DTO/IBigCommerceUpsertStoreDTO";
import IBigCommerceOnUpsertStoreDTO from "../../../../../models/DTO/IBigCommerceOnUpsertStoreDTO";
import INotification from "../../../../../models/INotification";
import NotificationLevelEnum from "../../../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../../../models/NotificationPositionEnum";
import ILoadUserDTO from "../../../../../models/DTO/ILoadUserDTO";
import { Socket } from "socket.io-client";

type Props = {
    closeModalFn: () => void;
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
    store?: IBigCommerce;
};

export const BigCommerceStoreModal: React.FC<Props> = (props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const [store, setStore] = useState(props.store || ({} as INewBigCommerce));
    const [catalogs, setCatalogs] = useState<ICatalog[]>([]);
    const [catalog, setCatalog] = useState<ICatalog>();
    const [saveActive, setSaveActive] = useState(false);
    const [existingStore, setExistingStore] = useState(!!props.store);

    useEffect(() => {
        props.socket.on(EndpointsEnum.CatalogsOnGetCatalogs, onGetCatalogs);
        props.socket.on(EndpointsEnum.BigCommerceOnUpsertStore, onUpsertStore);

        getCatalogs();

        return () => {
            props.socket.removeListener(EndpointsEnum.CatalogsOnGetCatalogs, onGetCatalogs);
            props.socket.removeListener(EndpointsEnum.BigCommerceOnUpsertStore, onUpsertStore);
        };
    }, []);

    const closeModal = () => {
        props.closeModalFn();
    };

    const getCatalogs = () => {
        const payload: IGetCatalogsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            callMeta: {
                view: "BigCommerce",
                fn: "getCatalogs",
            },
        };

        props.socket.emit(EndpointsEnum.CatalogsGetCatalogs, payload);
    };

    const onGetCatalogs = (data: IOnGetCatalogsDTO) => {
        setCatalogs(data.catalogs);

        if (store.catalog_id) {
            for (const cat of catalogs) {
                if (cat.id.toString() === store.catalog_id.toString()) {
                    setCatalog(cat);
                }
            }
        }
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;
        const name = e.target.name;

        const _store: any = { ...store };

        _store[name] = value;

        setStore(_store);

        if (name === "catalog_id") {
            for (const cat of catalogs) {
                if (cat.id.toString() === value.toString()) {
                    setCatalog(cat);
                }
            }
        }
    };

    const saveStore = () => {
        if (store.price_group_id) {
            const payload: IBigCommerceUpsertStoreDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                name: store.name,
                clientSecret: store.client_secret,
                clientId: store.client_id,
                active: store.active,
                priceGroupId: store.price_group_id,
                catalogId: store.catalog_id,
                apiPath: store.api_path,
                accessToken: store.access_token,
            };

            props.socket.emit(EndpointsEnum.BigCommerceUpsertStore, payload);
        }
    };

    const onUpsertStore = (data: IBigCommerceOnUpsertStoreDTO) => {
        const notification: INotification = {
            message: "successfully_processed_store",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        props.notifyFn(null, notification);

        props.closeModalFn();
    };

    return (
        <div className={"BigCommerceStoreModal p-2"}>
            <div className={"w-100"}>
                <div className={"d-flex justify-content-between"}>
                    <OwTitle leftIconName={"fad fa-cloud-upload-alt"} title={"add_store"} tag={"h3"} />
                    <div>
                        <OwButton className={"btn-danger"} centerIconClass={"fas fa-times"} fn={closeModal} />
                    </div>
                </div>
            </div>

            <div>
                <div className="mb-2">
                    <OwLabel title={"name"} />
                    <OwFnInput fn={handleChange} name={"name"} value={store.name} disabled={existingStore} />
                </div>

                <div className="mb-2">
                    <OwLabel title={"access_token"} />
                    <OwFnInput fn={handleChange} name={"access_token"} value={store.access_token} disabled={existingStore} />
                </div>

                <div className="mb-2">
                    <OwLabel title={"client_id"} />
                    <OwFnInput fn={handleChange} name={"client_id"} value={store.client_id} disabled={existingStore} />
                </div>

                <div className="mb-2">
                    <OwLabel title={"client_secret"} />
                    <OwFnInput fn={handleChange} name={"client_secret"} value={store.client_secret} disabled={existingStore} />
                </div>

                <div className="mb-2">
                    <OwLabel title={"api_path"} />
                    <OwFnInput fn={handleChange} name={"api_path"} value={store.api_path} disabled={existingStore} />
                </div>

                <div className="mb-2">
                    <OwLabel title={"catalog"} />
                    <OwSelectOption
                        name={"catalog_id"}
                        fn={handleChange}
                        options={catalogs}
                        valueProp={"id"}
                        titleProp={"name"}
                        selectedOption={store}
                        selectedOptionProp={"catalog_id"}
                    />
                </div>

                <div className="mb-2">
                    <OwLabel title={"price_group"} />
                    <OwSelectOption
                        name={"price_group_id"}
                        fn={handleChange}
                        options={catalog?.Price_Groups || []}
                        valueProp={"id"}
                        titleProp={"value"}
                        selectedOption={store}
                        selectedOptionProp={"price_group_id"}
                    />
                </div>

                <div className="mb-2">
                    <OwCheckbox
                        fn={handleChange}
                        name={"active"}
                        truthyVal={true}
                        falsyVal={false}
                        checked={store.active}
                        label={"active"}
                    />
                </div>

                <div className="mb-2">
                    <OwButton title={"save"} fn={saveStore} disableOnActive={true} active={saveActive} />
                </div>
            </div>
        </div>
    );
};
